import { useNavigate, useParams } from 'react-router-dom';

import { AlertTriangle, CheckCircle2, Edit, FileWarning, Users } from 'lucide-react';

import { rotuloDoTipoDeCredenciado } from '@/nucleo/pessoaNatureza';
import { DetailHeader } from '@/ui/DetailHeader';
import { DetailSummarySidebar } from '@/ui/DetailSummarySidebar';
import { DetailWithTimeline } from '@/ui/DetailWithTimeline';
import { EmptyState } from '@/ui/EmptyState';
import { ErrorState } from '@/ui/ErrorState';
import { Spinner } from '@/ui/Spinner';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Card, CardContent } from '@/ui/base/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/ui/base/tabs';

import { AbaDadosGerais } from './AbaDadosGerais';
import { AbaDamsp } from './AbaDamsp';
import { AbaDocumentos } from './AbaDocumentos';
import { AbaFrotaEEquipe } from './AbaFrotaEEquipe';
import { AbaHistorico } from './AbaHistorico';
import { AbaLinhaDoTempo } from './AbaLinhaDoTempo';
import { AbaMultas } from './AbaMultas';
import { AbaOrdensDeServico } from './AbaOrdensDeServico';
import { AbaProcessosSei } from './AbaProcessosSei';
import { AbaRecursos } from './AbaRecursos';
import { AbaRepresentantes } from './AbaRepresentantes';
import { AbaSuspensoes } from './AbaSuspensoes';
import { AbaTermos } from './AbaTermos';
import { AbaVisaoFinanceira } from './AbaVisaoFinanceira';
import { CicloDeCredenciamento } from './CicloDeCredenciamento';
import { situacaoComoSelo } from '@/modulos/operacao/comum/contrato';
import { useCredenciado, useRepresentantesDoCredenciado } from './dados';
import {
  useDocumentosDoCredenciado,
  useFrotaDoCredenciado,
  useGatingDoCredenciado,
  useOrdensDeServicoDoCredenciado,
  useProcessosSeiDoCredenciado,
  useTermosDoCredenciado,
  useVinculosDoCredenciado,
} from './detalheDados';

/**
 * `/operacao/credenciados/:slug` — a tela de detalhe do credenciado.
 *
 * O CABECALHO, A ABA DADOS GERAIS E A ABA REPRESENTANTES LEEM A API
 * (`dados.ts`, `bff/v1/operacao/credenciados`). O documento chega mascarado; os
 * contatos, inteiros, e o banco registra a leitura em `acessos_dado_pessoal`. Quem e
 * de DRE e abre credenciado de outra diretoria recebe "Credenciado nao encontrado":
 * o recorte e do banco.
 *
 * AS DEMAIS ABAS continuam com o layout migrado do protótipo e mock de leitura
 * (`detalheDados.ts`), pela excecao registrada em CLAUDE.md, "Proibido", em
 * 2026-08-27 — termos, OS, SEI, DAMSP, multas, recursos, frota, documentos,
 * suspensoes, historico. O widget "Ciclo de Credenciamento" mostra os marcos sem
 * data: as datas vivem em tabelas da jornada que esta tela ainda nao le. Nenhuma
 * acao de escrita grava nada: cada uma mostra o aviso de indisponibilidade certo.
 */
export function CredenciadoDetail() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const { data: credenciado, isLoading, isError, error, refetch } = useCredenciado(slug);
  const credenciadoId = credenciado?.id;

  const { data: frota } = useFrotaDoCredenciado(credenciadoId);
  const { data: vinculos } = useVinculosDoCredenciado(credenciadoId);
  const { data: representantes } = useRepresentantesDoCredenciado(credenciadoId);
  const { data: termos } = useTermosDoCredenciado(credenciadoId);
  const { data: ordens } = useOrdensDeServicoDoCredenciado(credenciadoId);
  const { data: processosSei } = useProcessosSeiDoCredenciado(credenciadoId);
  const { data: documentos } = useDocumentosDoCredenciado(credenciadoId);
  const { data: gating } = useGatingDoCredenciado(credenciadoId, credenciado?.status);

  if (isLoading) {
    return <Spinner size="lg" fullPage />;
  }

  if (isError) {
    return (
      <ErrorState
        variant="load-failed"
        description={
          error?.aviso ?? 'Não foi possível buscar este credenciado agora. Tente novamente.'
        }
        primaryAction={{ label: 'Tentar de novo', onClick: () => void refetch() }}
      />
    );
  }

  if (!credenciado) {
    return (
      <EmptyState
        icon={Users}
        title="Credenciado não encontrado"
        description="O endereço pode estar incorreto, ou o credenciado pode ter sido removido."
        actionLabel="Voltar para Credenciados"
        onAction={() => navigate('/operacao/credenciados')}
      />
    );
  }

  const condutoresAtivos =
    vinculos?.filter((v) => v.tipo_pessoa_vinculada === 'CONDUTOR' && v.status_vinculo === 'ATIVO')
      .length ?? 0;
  const monitoresAtivos =
    vinculos?.filter((v) => v.tipo_pessoa_vinculada === 'MONITOR' && v.status_vinculo === 'ATIVO')
      .length ?? 0;
  const representantesAtivos = representantes?.filter((r) => r.ativo).length ?? 0;
  const documentacaoPendente =
    documentos?.some((d) => d.obrigatorio && d.status !== 'aprovado') ?? false;

  return (
    <div className="space-y-4">
      <DetailHeader
        backTo="/operacao/credenciados"
        backLabel="Credenciados"
        title={credenciado.nome}
        subtitle={
          <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <span className="text-foreground font-mono">{credenciado.cpf_cnpj || '—'}</span>
            <span className="text-muted-foreground">·</span>
            <span>{rotuloDoTipoDeCredenciado(credenciado.tipo)}</span>
            {credenciado.termo_permissionario && (
              <>
                <span className="text-muted-foreground">·</span>
                <span>TP {credenciado.termo_permissionario}</span>
              </>
            )}
          </span>
        }
        status={{ variant: situacaoComoSelo(credenciado.status), label: credenciado.status }}
        actions={
          <div className="flex items-center gap-2">
            <Badge
              variant="outline"
              className={
                documentacaoPendente
                  ? 'border-warning/40 bg-warning/5 text-warning gap-1 font-medium'
                  : 'border-success/40 bg-success/5 text-success gap-1 font-medium'
              }
            >
              {documentacaoPendente ? (
                <AlertTriangle className="h-3 w-3" aria-hidden="true" />
              ) : (
                <CheckCircle2 className="h-3 w-3" aria-hidden="true" />
              )}
              {documentacaoPendente ? 'Documentação pendente' : 'Documentação completa'}
            </Badge>
            <Button onClick={() => navigate(`/operacao/credenciados/${slug}/editar`)}>
              <Edit className="mr-2 h-4 w-4" aria-hidden="true" /> Editar
            </Button>
          </div>
        }
      />

      <DetailWithTimeline
        timeline={
          <>
            <CicloDeCredenciamento credenciado={credenciado} gating={gating} />
            <DetailSummarySidebar
              items={[
                { label: 'Tipo', value: rotuloDoTipoDeCredenciado(credenciado.tipo) },
                { label: 'Status', value: credenciado.status },
                {
                  label: 'Documento',
                  value: <span className="font-mono">{credenciado.cpf_cnpj || '—'}</span>,
                },
                frota && { label: 'Veículos', value: frota.length },
                { label: 'Representantes ativos', value: representantesAtivos },
                { label: 'Vínculos ativos', value: condutoresAtivos + monitoresAtivos },
                termos && { label: 'Termos', value: termos.length },
                ordens && { label: 'Ordens de serviço', value: ordens.length },
                processosSei && { label: 'Processos SEI', value: processosSei.length },
              ]}
              audit={{ createdAt: credenciado.created_at, updatedAt: credenciado.updated_at }}
            />
          </>
        }
        summary={
          <Card>
            <CardContent className="grid gap-4 p-4 md:grid-cols-4">
              <div>
                <p className="text-muted-foreground text-xs">Veículos</p>
                <p className="text-foreground text-lg font-semibold">{frota?.length ?? 0}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">Representantes ativos</p>
                <p className="text-foreground text-lg font-semibold">{representantesAtivos}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">Vínculos ativos</p>
                <p className="text-foreground text-lg font-semibold">
                  {condutoresAtivos + monitoresAtivos}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">Situação operacional</p>
                <p className="text-foreground text-sm font-semibold">
                  {gating?.pode_operar ? 'Liberada' : 'Bloqueada'}
                </p>
                <p className="text-muted-foreground text-xs">
                  {gating?.motivos?.[0] ?? 'Sem bloqueios críticos'}
                </p>
              </div>
            </CardContent>
          </Card>
        }
      >
        <Tabs defaultValue="dados">
          <TabsList>
            <TabsTrigger value="dados">Dados Gerais</TabsTrigger>
            <TabsTrigger value="termos">Termos</TabsTrigger>
            <TabsTrigger value="financeiro">Visão Financeira</TabsTrigger>
            <TabsTrigger value="os">Ordens de Serviço</TabsTrigger>
            <TabsTrigger value="sei">Processos SEI</TabsTrigger>
            <TabsTrigger value="damsp">DAMSP</TabsTrigger>
            <TabsTrigger value="multas">Multas</TabsTrigger>
            <TabsTrigger value="recursos">Recursos</TabsTrigger>
            <TabsTrigger value="frota">Frota &amp; Equipe</TabsTrigger>
            <TabsTrigger value="representantes">Representantes</TabsTrigger>
            <TabsTrigger value="documentos">
              Documentos
              {documentacaoPendente && (
                <FileWarning className="text-warning ml-1.5 h-3.5 w-3.5" aria-hidden="true" />
              )}
            </TabsTrigger>
            <TabsTrigger value="suspensoes">Suspensões</TabsTrigger>
            <TabsTrigger value="historico">Histórico</TabsTrigger>
            <TabsTrigger value="linha-tempo">Linha do tempo</TabsTrigger>
          </TabsList>

          <TabsContent value="dados">
            <AbaDadosGerais credenciado={credenciado} />
          </TabsContent>
          <TabsContent value="termos">
            <AbaTermos credenciadoId={credenciado.id} />
          </TabsContent>
          <TabsContent value="financeiro">
            <AbaVisaoFinanceira credenciadoId={credenciado.id} />
          </TabsContent>
          <TabsContent value="os">
            <AbaOrdensDeServico credenciadoId={credenciado.id} />
          </TabsContent>
          <TabsContent value="sei">
            <AbaProcessosSei credenciadoId={credenciado.id} />
          </TabsContent>
          <TabsContent value="damsp">
            <AbaDamsp credenciadoId={credenciado.id} />
          </TabsContent>
          <TabsContent value="multas">
            <AbaMultas credenciadoId={credenciado.id} />
          </TabsContent>
          <TabsContent value="recursos">
            <AbaRecursos credenciadoId={credenciado.id} />
          </TabsContent>
          <TabsContent value="frota">
            <AbaFrotaEEquipe credenciadoId={credenciado.id} />
          </TabsContent>
          <TabsContent value="representantes">
            <AbaRepresentantes
              credenciadoId={credenciado.id}
              tipoDoCredenciado={credenciado.tipo}
            />
          </TabsContent>
          <TabsContent value="documentos">
            <AbaDocumentos credenciadoId={credenciado.id} nomeDoCredenciado={credenciado.nome} />
          </TabsContent>
          <TabsContent value="suspensoes">
            <AbaSuspensoes
              credenciadoId={credenciado.id}
              statusDoCredenciado={credenciado.status}
            />
          </TabsContent>
          <TabsContent value="historico">
            <AbaHistorico credenciadoId={credenciado.id} />
          </TabsContent>
          <TabsContent value="linha-tempo">
            <AbaLinhaDoTempo credenciadoId={credenciado.id} />
          </TabsContent>
        </Tabs>
      </DetailWithTimeline>
    </div>
  );
}

export default CredenciadoDetail;
